/**
 * useQuickCapture — Cmd+T / Ctrl+T global keybinding (F 72 MVP phase 4)
 *
 * Attaches a window keydown listener and toggles `opened` state.
 *
 * Notes:
 *   - Cmd+T (mac) and Ctrl+T (win/linux) are normally browser-reserved
 *     (open new tab). We preventDefault to override.
 *   - On macOS, Cmd vs Meta: we accept both 'Meta' (Cmd) and 'Control'.
 *   - The hook is idempotent — multiple callers share one handler per
 *     window. Use the returned `opened` + `open` / `close` / `toggle`.
 */

import { useEffect, useState, useCallback } from 'react';

const HANDLER_ATTR = 'data-fc-quick-capture-installed';

function isCaptureShortcut(e: KeyboardEvent): boolean {
  if (e.key !== 't' && e.key !== 'T') return false;
  if (e.altKey) return false; // Alt+T is for terminal on some systems
  // Cmd+T on mac, Ctrl+T elsewhere (F 69 §F 73 locked — DO NOT remove).
  // Cmd+Shift+T (and Ctrl+Shift+T) is a browser-safe alternative added in
  // F 77 because some browsers (Vivaldi confirmed) intercept plain Cmd+T
  // before our keydown handler fires. STOP condition from §F 73: 'hotkey
  // collision with browser defaults' — anticipated; this is the mitigation.
  if (e.shiftKey) {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
    if (isMac) return e.metaKey && !e.ctrlKey;
    return e.ctrlKey && !e.metaKey;
  }
  // Cmd+T on mac, Ctrl+T elsewhere — original spec
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  if (isMac) return e.metaKey && !e.ctrlKey;
  return e.ctrlKey && !e.metaKey;
}

export interface UseQuickCaptureResult {
  opened: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Install the global Cmd+T handler once. Multiple components calling this
 * hook share the same handler via a window-level marker. Returns the
 * shared opened-state control.
 */
export function useQuickCapture(): UseQuickCaptureResult {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    const w = window as any;
    if (w[HANDLER_ATTR]) return; // already installed by another component

    const handler = (e: KeyboardEvent) => {
      if (!isCaptureShortcut(e)) return;
      e.preventDefault();
      const ev = new CustomEvent('fc:quick-capture:toggle');
      window.dispatchEvent(ev);
    };

    window.addEventListener('keydown', handler);
    w[HANDLER_ATTR] = true;

    const toggleHandler = () => setOpened((prev) => !prev);
    window.addEventListener('fc:quick-capture:toggle', toggleHandler);

    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('fc:quick-capture:toggle', toggleHandler);
      w[HANDLER_ATTR] = false;
    };
  }, []);

  const open = useCallback(() => setOpened(true), []);
  const close = useCallback(() => setOpened(false), []);
  const toggle = useCallback(() => setOpened((p) => !p), []);

  return { opened, open, close, toggle };
}

/** Pure helper for testing the shortcut logic. */
export const _internals = { isCaptureShortcut };
